import React, { useEffect, useState, createContext, useContext } from "react";
import { SplashCarregando } from '~/components/UI/SplashCarregando';
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { LocalCacheHandler } from '~/utils/local-cache-handler';
import { NotificationProvider } from './context/NotificationContext';
import { JobProgressProvider } from './context/JobProgressContext';

import {
  clearAllDepartmentsCalendarState,
  loadAllDepartmentsCalendarTasks,
} from "./_domains/calendarTasks/actions";
import { loadCertificates } from "./components/Certificates/actions";
import { loadConventions } from "./components/Conventions/actions";
import { loadProcurations } from "./components/Procurations/actions";
import { loadTasks } from "./components/RegisterTasks/actions";
import { loadUser, loadUsers } from "./components/Users/actions";
import { ROUTES } from "./constants/routes.constants";
import { route } from "./_shared/components/MainMenu/routes";
import { loadChecklistTasks } from './containers/Checklist/actions';
import { loadCustomers } from "./_domains/customers/drawer/actions";
import { loadDocumentTypes } from "./containers/Documents/actions";
import { loadLinkCategories, loadLinks } from "./containers/Links/actions";
import { loadPermissionsGroups } from "./containers/Permissions/actions";
import { loadEmailsLogs } from "./_domains/emailsLogs/actions";
import { loadSettings } from "./containers/Settings/actions";
import { AccountStorage } from './storages/account';
import { handleLogout } from './utils/handle-logout';
import { CACHE_ACTIONS } from './constants/cache-actions.constants';
import { loadSolicitations } from "~/_domains/solicitations/actions";
import { getStats as getBankReconciliationStats } from "~/screens/Financial/BankReconciliation/actions";
import { data as defaultOnboardingData } from './_domains/onboarding/StartHereGraphic/guide';
import { updateOnboardingStep } from './_domains/onboarding/actions';

export const DataContext = createContext();

export const AppContext = ({ children, location }) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const { user } = useSelector((state) => state);
  const onboardingData = Array.isArray(user.onboarding_status) ? user.onboarding_status : defaultOnboardingData;
  const [unlockedMenus, setUnlockedMenus] = useState([]);

  useEffect(() => {
    if (user.id) {
      const onboardingStatusArray = Array.isArray(user.onboarding_status) ? user.onboarding_status : [];
      const unlockedMenusDone = onboardingStatusArray
        .filter(task => task.status === 'done')
        .flatMap(task => task.unlockMenus || []);
      setUnlockedMenus(unlockedMenusDone);
    }
  }, [user]);

  async function loadInitialCache() {
    
    const cacheActionKeys = Object.keys(CACHE_ACTIONS);

    await Promise.all(cacheActionKeys.map(async (key) => {
      const actionName = CACHE_ACTIONS[key];
      const pathname = CACHE_ACTIONS[key];

      await LocalCacheHandler.load({
        actionName,
        dispatch,
        pathname,
      });
    }));

  }

  // function refreshEach5Minutes() {
  //   setInterval(() => {
  //     dispatch(loadSolicitations());
  //   }, 600000); // 10 minutes
  // }

  // useEffect(() => {
  //   refreshEach5Minutes();
  // }, []);

  function getLoaders() {
    const loadersToCall = [];
    const loaders = [
      loadUser,
      loadSettings,
      loadPermissionsGroups,
      loadAllDepartmentsCalendarTasks,
      loadCustomers,
      loadSolicitations,
      loadCertificates,
      loadProcurations,
      loadConventions,
      loadUsers,
      loadLinks,
      loadLinkCategories,
      loadDocumentTypes,
      loadChecklistTasks,
      loadTasks,
      loadEmailsLogs,
      // loadPayments is not here: it needs the page's filter (it threw when called
      // with no args, aborting this loop). Mensalidades loads it on mount.
      getBankReconciliationStats
    ];

    const { pathname } = location;

    loaders.forEach((loader) => {
      // Conciliação loads stats for its restored bank on mount; this unfiltered
      // call could land after it and overwrite them.
      if (loader === getBankReconciliationStats && pathname === ROUTES["/financeiro/conciliacao"]) {
        return;
      }
      const permissionsGroupsWillAlreadyBeCalled =
        loader.name === "loadPermissionsGroups" &&
        pathname === ROUTES["/checklist"];
      const conventionsWillAlreadyBeCalled =
        loader === loadConventions && pathname === route.convencoes;
      // Certificados and Lembretes live under /societario/* (the old /certificados
      // and /lembretes paths have no route). Their screens load on mount with the
      // restored filter; an unfiltered call here could land after it. Compared by
      // reference: minification renames functions, so loader.name is unreliable.
      const certificadosWillAlreadyBeCalled =
        loader === loadCertificates &&
        (pathname === route.societario.certificados || pathname === ROUTES["/certificados"]);
      const procurationsWillAlreadyBeCalled =
        loader === loadProcurations &&
        (pathname === route.societario.lembretes || pathname === ROUTES["/lembretes"]);
      const linksWillAlreadyBeCalled =
        loader.name === "loadLinks" && pathname === ROUTES["/links"];
      const linkCategoriesWillAlreadyBeCalled =
        loader.name === "loadLinkCategories" && pathname === ROUTES["/links"];
      const tasksWillAlreadyBeCalled =
        loader.name === "loadTasks" && pathname === ROUTES["/tarefas"];

      if (
        !permissionsGroupsWillAlreadyBeCalled &&
        !conventionsWillAlreadyBeCalled &&
        !certificadosWillAlreadyBeCalled &&
        !procurationsWillAlreadyBeCalled &&
        !linksWillAlreadyBeCalled &&
        !linkCategoriesWillAlreadyBeCalled &&
        !tasksWillAlreadyBeCalled
      ) {
        loadersToCall.push(dispatch(loader()));
      }
    });

    return loadersToCall;
  }

  async function loadInitialData() {
    await loadInitialCache();

    try {
      await Promise.all(getLoaders());
    } catch (error) {
      if (
        error.error &&
        error.error.response &&
        (error.error.response.status === 401 || error.error.response.status === 400)
      ) {
        handleLogout();
        history.replace("/login");
      }
    }
  }

  useEffect(() => {
    if (AccountStorage.get().token) {
      loadInitialData();
    } else {
      handleLogout();
      history.replace("/login");
    }

    return () => dispatch(clearAllDepartmentsCalendarState());
  }, []);

  function setOnboardingTaskAsDone(taskId) {
    if(onboardingData.length === 0) return;
    const updatedData = onboardingData.map(task =>
      task.id === taskId ? { ...task, status: 'done' } : task
    );
    dispatch(updateOnboardingStep(updatedData));
  }

  function setAllOnboardingTasksAsDone() {
    if(onboardingData.length === 0) return;
    const updatedData = onboardingData.map(task =>
      task.status === 'pending' ? { ...task, status: 'done' } : task
    );
    dispatch(updateOnboardingStep(updatedData));
  }

  function setOnboardingTaskAsClicked(taskId) {
    if(onboardingData.length === 0) return;
    const updatedData = onboardingData.map(task =>
      task.id === taskId ? { ...task, clicked: true } : task
    );
    dispatch(updateOnboardingStep(updatedData));
  }

  function resetOnboardingTasks() {
    if(onboardingData.length === 0) return;
    const updatedData = onboardingData.map(task => ({ ...task, status: 'pending' }));
    dispatch(updateOnboardingStep(updatedData));
  }

  // Load IssueWidget script
  const [isIssueWidgetReady, setIsIssueWidgetReady] = useState(false);

  useEffect(function() {
    if (!document.getElementById('issue-widget-script')) {
      var script = document.createElement('script');
      script.id = 'issue-widget-script';
      script.src = 'https://gestao.redhmam.com/widget/widget.js?v=1.1.0';
      script.setAttribute('data-token', '37769020-f60f-4c9a-94f2-4cf044803866');
      script.setAttribute('data-hide-button', 'true');
      script.onload = function() {
        // Check if the widget class is available and instantiate it
        var checkInterval = setInterval(function() {
          if (window.IssueWidget && typeof window.IssueWidget === 'function') {
            // Instantiate the widget and call init() to set up the API
            var widget = new window.IssueWidget({
              token: '37769020-f60f-4c9a-94f2-4cf044803866',
              hideButton: true,
              userMetadata: {
                email: user.email,
                name: user.name
              }
            });
            widget.init();
            setIsIssueWidgetReady(true);
            clearInterval(checkInterval);
          } else if (window.IssueWidgetAPI) {
            setIsIssueWidgetReady(true);
            clearInterval(checkInterval);
          }
        }, 100);
        // Stop checking after 5 seconds
        setTimeout(function() {
          clearInterval(checkInterval);
        }, 5000);
      };
      document.body.appendChild(script);
    }
  }, [user.email, user.name]);

  return (
    <NotificationProvider>
      <JobProgressProvider>
        <DataContext.Provider value={{ setOnboardingTaskAsDone, setAllOnboardingTasksAsDone, setOnboardingTaskAsClicked, resetOnboardingTasks, unlockedMenus, onboardingData, isIssueWidgetReady }}>
          {!user.id && <SplashCarregando />}
          {children}
        </DataContext.Provider>
      </JobProgressProvider>
    </NotificationProvider>
  );
};

export const useAppContext = () => {
  const context = useContext(DataContext);
  if (!context) {
    throw new Error("useAppContext must be used within a AppContextProvider");
  }
  return context;
};